import { type TabularColumnConfig, type TabularViewData } from "../../../../models/models";

import { PopperTooltip } from "../../../../../../common/components/Tooltip";
import { type ColumnDef } from "@tanstack/react-table";

import { isNonNull } from "~/common/utils/reducers";
import { SearchResultListItemNew } from "../ListView/components/SearchResultListItemNew/SearchResultListItemNew";
import {
  styled,
  TableSortLabel as MuiTableSortLabel,
  type TableSortLabelProps as MuiTableSortLabelProps,
} from "~/common/mui";
import { SortDirection } from "~/common/hooks/useSorting";

import { type MouseEvent, type ReactNode, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "~/app/store";
import { C_ON_SURFACE } from "~/common/components/icon/Colors";
import { changeSort } from "~/features/search/redux/actions";
import classNames from "classnames";
import { ConfigureColumnsButton } from "~/features/search/components/ProductResults/views/NewTabularView/ConfigureColumnsButton/ConfigureColumnsButton";
import { renderTechValue } from "~/utils/renderTechValue";
import { lineClamp } from "~/common/components/icon/FontUtils";

const TECH_VALUE_COLUMN_WIDTH = 126;

export const tsmakeNewDesignProductColumn = (label: string): ColumnDef<TabularViewData> => {
  return {
    id: "name",
    header: () => <DesignationHeaderCell label={label} />,
    size: 814,
    cell: (cell) => (
      <NewDesignProductColumn
        productCode={cell.row.original.product.code}
        index={cell.row.index}
        searchQuery={""}
        isTechnicalView
      />
    ),
  };
};

export const DesignationHeaderCell = ({ className, label }: { className?: string; label: string }) => {
  return (
    <div className={classNames("d-flex", className)}>
      <HeaderCell
        label={label}
        sortAscCode={"sxTitleAttribute-asc"}
        sortDescCode={"sxTitleAttribute-desc"}
        data-test-id={"product"}
      />
      <ConfigureColumnsButton className={"ml-auto"} />
    </div>
  );
};

const NewDesignProductColumn = styled(SearchResultListItemNew)({
  margin: "-8px",
  border: "none",
  ["& .MuiCardContent-root"]: {
    padding: "16px",
  },
});

export const makeTechValueColumn = (colDef: TabularColumnConfig): ColumnDef<TabularViewData> => {
  return {
    id: colDef.techValueUID,
    size: TECH_VALUE_COLUMN_WIDTH,
    header: () => <TechValHeaderCell config={colDef} alignment={"right"} />,
    cell: (cell) => <TechValueCell col={colDef} data={cell.row.original} />,
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const calculateTotalTableWidth = (columnConfig: ColumnDef<any>[]) => {
  const sizes = columnConfig.map((c) => c.size).filter(isNonNull);
  if (sizes.length > 0 && sizes.some((s) => s < 1)) {
    throw new Error("Not implemented");
  }
  return sizes.reduce((a, b) => a + b, 0);
};

interface TechValueCellProps {
  col: TabularColumnConfig;
  data: TabularViewData;
}

const TechValueCell = ({ col, data }: TechValueCellProps) => {
  const { abbreviation, value = "-", isConverted } = data.techValues[col.techValueUID] ?? {};

  const isInch = col.unit?.toLowerCase() == "in";

  const label = renderTechValue(value, isInch, isConverted);

  if (abbreviation) {
    const abbreviationCmp = <span dangerouslySetInnerHTML={{ __html: abbreviation }} />;

    return (
      <PopperTooltip
        tooltipContent={<span className={"f-no-wrap"}>{abbreviationCmp}</span>}
        className={isConverted ? "d-inline f-info-color" : "d-inline"}
      >
        {label} <span dangerouslySetInnerHTML={{ __html: col.unit ?? "" }} />
      </PopperTooltip>
    );
  }

  return label;
};

interface TechValHeaderCellProps {
  config: TabularColumnConfig;
  alignment?: "right" | "left";
  className?: string;
}

export const TechValHeaderCell = ({ config, alignment, className }: TechValHeaderCellProps) => {
  const { t } = useTranslation();

  const hoverTitle = t(config.hoverTitle);

  return (
    <HeaderCell
      alignment={alignment}
      hoverTitle={<div dangerouslySetInnerHTML={{ __html: hoverTitle }} />}
      label={<ClipText dangerouslySetInnerHTML={{ __html: hoverTitle }} />}
      sortDescCode={config.sortDesc}
      sortAscCode={config.sortAsc}
      data-test-id={config.techValueUID}
    />
  );
};

const ClipText = styled("div")({
  ...lineClamp(2),
});

interface HeaderCellProps {
  label: ReactNode;
  hoverTitle?: ReactNode;
  sortDescCode?: string;
  sortAscCode?: string;
  alignment?: "left" | "right";
  "data-test-id": string;
  unit?: string;
  className?: string;
}

export const HeaderCell = ({
  hoverTitle,
  label,
  sortAscCode,
  sortDescCode,
  className,
  "data-test-id": testId,
}: HeaderCellProps) => {
  const selectedSortCode = useSelector(selectSortCode);
  const selectedSortDirection = useSelector(selectSortDirection);

  const selectedSort = selectedSortDirection ? selectedSortCode + selectedSortDirection : selectedSortCode;
  const selectedAsc = selectedSort && selectedSort === sortAscCode;
  const selectedDesc = selectedSort && selectedSort === sortDescCode;

  const isSelected = selectedAsc || selectedDesc;

  const dispatch = useDispatch();

  const onClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      const canClickAsc = sortAscCode && selectedSort !== sortAscCode;
      const canClickDesc = sortDescCode && selectedSort !== sortDescCode;
      const selectedAsc = selectedSort && selectedSort === sortAscCode;
      const selectedDesc = selectedSort && selectedSort === sortDescCode;
      if (canClickAsc && !selectedAsc && sortAscCode) {
        const parts = sortAscCode.split("-", 2);
        // @ts-ignore
        dispatch(changeSort(parts[0], parts[1] ? "-" + parts[1] : undefined));
      } else if (canClickDesc && !selectedDesc && sortDescCode) {
        const parts = sortDescCode.split("-", 2);
        // @ts-ignore
        dispatch(changeSort(parts[0], parts[1] ? "-" + parts[1] : undefined));
      }
    },
    [sortAscCode, sortDescCode, selectedSort],
  );

  const testIdId = `sort-btn-${testId}`;

  const testIdSorting = `sorting-${selectedDesc ? "descending" : "ascending"} ${isSelected ? "sorting-selected" : ""}`;

  if (sortAscCode || sortDescCode) {
    label = (
      <TableSortLabel
        className="scha__ellipsis-text"
        onClick={onClick}
        active={!!isSelected}
        data-test-id={testIdSorting}
        direction={selectedDesc ? SortDirection.Desc : SortDirection.Asc}
      >
        {label}
      </TableSortLabel>
    );
  }

  if (hoverTitle) {
    label = (
      <PopperTooltip tooltipContent={hoverTitle} className={"d-i-flex scha__ellipsis-text"}>
        {label}
      </PopperTooltip>
    );
  }

  return (
    <div data-test-id={testIdId} className={classNames(className, "d-flex")}>
      {label}
    </div>
  );
};

const selectSortCode = (state: RootState) => state.srp.sort.selectedCode;
const selectSortDirection = (state: RootState) => state.srp.sort.selectedDirection;

export const TableSortLabel = (props: MuiTableSortLabelProps) => {
  return (
    <MuiTableSortLabel
      {...props}
      sx={{
        color: C_ON_SURFACE,
        alignItems: "flex-start",
        "& .MuiTableSortLabel-icon": {
          marginLeft: "4px",
          marginRight: "2px",
          fontSize: "20px !important",
          color: "inherit !important",
        },
      }}
      slotProps={{
        icon: {
          fontSize: "medium",
        },
      }}
    />
  );
};
